<script setup>
/**
 * The builder's Design tab: theme color (preset swatches + custom hex),
 * the campaign image, and the campaign's run settings. The former Settings
 * tab (enabled switch + start/end window) lives here as the closing
 * section so the rail stays at three tabs — the top bar's Schedule button
 * jumps straight to it.
 */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import Input from 'dashboard/components-next/input/Input.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import ImageEditor from './ImageEditor.vue';
import {
  DEFAULT_THEME_COLOR,
  THEME_COLOR_PRESETS,
  isValidThemeColor,
} from 'dashboard/components-next/Campaigns/helpers/campaignContent';

const themeColor = defineModel('themeColor', { type: String, default: '' });
const imageUrl = defineModel('imageUrl', { type: String, default: '' });
const imageAlt = defineModel('imageAlt', { type: String, default: '' });
const enabled = defineModel('enabled', { type: Boolean, default: true });
const startsAt = defineModel('startsAt', { type: String, default: '' });
const endsAt = defineModel('endsAt', { type: String, default: '' });

const { t } = useI18n();

// '' means "product default" — the default swatch reads as selected then.
const activeColor = computed(() => themeColor.value || DEFAULT_THEME_COLOR);

const pickSwatch = color => {
  themeColor.value = color === DEFAULT_THEME_COLOR ? '' : color;
};

const hexError = computed(() =>
  themeColor.value && !isValidThemeColor(themeColor.value)
    ? t('CAMPAIGN.BUILDER.DESIGN.THEME.CUSTOM_ERROR')
    : ''
);

const swatchClasses = isActive => [
  'size-8 rounded-full transition-shadow',
  isActive
    ? 'outline outline-2 outline-offset-2 outline-n-violet-9'
    : 'hover:outline hover:outline-2 hover:outline-offset-2 hover:outline-n-slate-6',
];
</script>

<template>
  <div class="flex flex-col gap-5">
    <fieldset class="flex flex-col min-w-0 gap-2">
      <legend class="mb-0.5 text-heading-3 text-n-slate-12">
        {{ t('CAMPAIGN.BUILDER.DESIGN.THEME.TITLE') }}
      </legend>
      <p class="mb-1 text-xs text-n-slate-11">
        {{ t('CAMPAIGN.BUILDER.DESIGN.THEME.DESCRIPTION') }}
      </p>

      <div
        role="group"
        :aria-label="t('CAMPAIGN.BUILDER.DESIGN.THEME.GROUP_LABEL')"
        class="flex items-center gap-2.5 px-1 py-1.5"
      >
        <button
          v-for="color in THEME_COLOR_PRESETS"
          :key="color"
          type="button"
          :class="swatchClasses(activeColor === color)"
          :style="{ backgroundColor: color }"
          :aria-pressed="activeColor === color"
          :aria-label="
            t('CAMPAIGN.BUILDER.DESIGN.THEME.SWATCH_LABEL', { color })
          "
          @click="pickSwatch(color)"
        />
      </div>

      <Input
        v-model="themeColor"
        :label="t('CAMPAIGN.BUILDER.DESIGN.THEME.CUSTOM_LABEL')"
        :placeholder="DEFAULT_THEME_COLOR"
        :message="hexError"
        :message-type="hexError ? 'error' : 'info'"
      />
    </fieldset>

    <fieldset class="flex flex-col min-w-0 gap-2">
      <legend class="mb-0.5 text-heading-3 text-n-slate-12">
        {{ t('CAMPAIGN.BUILDER.DESIGN.IMAGE.TITLE') }}
      </legend>
      <ImageEditor v-model:image-url="imageUrl" v-model:image-alt="imageAlt" />
    </fieldset>

    <fieldset class="flex flex-col min-w-0 gap-1">
      <legend class="mb-1.5 text-heading-3 text-n-slate-12">
        {{ t('CAMPAIGN.BUILDER.SETTINGS.ENABLED.TITLE') }}
      </legend>
      <div
        class="flex items-center justify-between gap-3 px-3 py-2.5 border rounded-lg border-n-weak bg-n-alpha-black2"
      >
        <div class="flex flex-col gap-0.5">
          <span id="builderEnabled" class="text-body-main text-n-slate-12">
            {{ t('CAMPAIGN.BUILDER.SETTINGS.ENABLED.LABEL') }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ t('CAMPAIGN.BUILDER.SETTINGS.ENABLED.DESCRIPTION') }}
          </span>
        </div>
        <Switch v-model="enabled" aria-labelledby="builderEnabled" />
      </div>
    </fieldset>

    <fieldset class="flex flex-col min-w-0 gap-3">
      <legend class="mb-0.5 text-heading-3 text-n-slate-12">
        {{ t('CAMPAIGN.BUILDER.SETTINGS.SCHEDULE.TITLE') }}
      </legend>
      <p class="mb-0 text-xs text-n-slate-11">
        {{ t('CAMPAIGN.BUILDER.SETTINGS.SCHEDULE.DESCRIPTION') }}
      </p>

      <Input
        v-model="startsAt"
        type="datetime-local"
        :label="t('CAMPAIGN.BUILDER.SETTINGS.SCHEDULE.STARTS_AT')"
      />

      <Input
        v-model="endsAt"
        type="datetime-local"
        :label="t('CAMPAIGN.BUILDER.SETTINGS.SCHEDULE.ENDS_AT')"
      />
    </fieldset>
  </div>
</template>
